<script lang="ts">
  import { mdiCheckAll, mdiPlusCircleOutline } from "@mdi/js"
  import StatusTag from "@kontextwerk/web-sdk/ui/display/StatusTag.svelte"
  import { RICH_TOOLTIP_CLASS } from "../../../../../../../../../_shared/_logic/rich-tooltips"
  import type {
    ContactOrderChannelToggle,
    ContactOrderDigitalChannel,
  } from "../../../../../../_logic/planning-selection-contract"

  type Props = {
    hasContactOrder?: boolean
    channels: ContactOrderChannelToggle[]
    planningPreviewError?: string | null
    customerInformationMode?: string | null
    customerInformationLabel?: string | null
    customerInformationDetail?: string | null
    disabled?: boolean
    pending?: boolean
    onToggle: (customerId: string, channel: ContactOrderDigitalChannel, event: MouseEvent) => void
  }

  const {
    hasContactOrder = false,
    channels,
    planningPreviewError = null,
    customerInformationMode = null,
    customerInformationLabel = null,
    customerInformationDetail = null,
    disabled = false,
    pending = false,
    onToggle,
  }: Props = $props()

  const confirmationInformation = $derived(
    String(customerInformationMode ?? "").startsWith("confirmation"),
  )
</script>

<div class="planning-cell" role="group" aria-label="Digitale Ankündigung" aria-busy={pending}>
  {#if confirmationInformation}
    <span class="confirmation-information-tag">
      <StatusTag
        size="sm"
        label={customerInformationLabel || "Bestätigung wird per E-Mail übermittelt"}
        subLabel={customerInformationDetail || undefined}
        color={customerInformationMode === "confirmation_no_email" ? "amber" : "neutral"}
        icon={customerInformationMode === "confirmation_no_email" ? undefined : mdiCheckAll}
        tooltipContent={customerInformationDetail || customerInformationLabel || undefined}
      />
    </span>
  {:else if hasContactOrder}
    <StatusTag
      size="sm"
      label="Bereits angelegt"
      color="neutral"
      tooltipContent="Für diesen Termin gibt es bereits einen Ankündigungsplan."
    />
  {:else}
    <div class="planning-tags">
      {#each channels as channel (channel.key)}
        <div class="channel-preview">
        <button
            type="button"
            class="selectable-status-tag-button"
            disabled={disabled || pending || Boolean(channel.disabledReason)}
            aria-pressed={channel.active}
            onclick={(event) => onToggle(channel.customerId, channel.channel, event)}
          >
          <StatusTag
              variant="status"
              size="sm"
              label={channel.label}
              topLabel={channel.topLabel}
              subLabel={channel.subLabel ?? undefined}
              color={channel.color}
              icon={channel.active ? mdiCheckAll : mdiPlusCircleOutline}
              tooltipContent={channel.disabledReason ?? channel.tooltip}
              tooltipHtml={channel.disabledReason ? false : (channel.tooltipHtml ?? false)}
              tooltipClassName={channel.disabledReason
                ? ""
                : channel.tooltipHtml
                  ? RICH_TOOLTIP_CLASS
                  : ""}
              disableTitle={true}
            />
        </button>
        </div>
      {/each}
      {#if !channels.length}
        <StatusTag
          size="sm"
          label="Kein digitaler Kanal verfügbar"
          color="amber"
          tooltipContent="Für diese Auswahl ist kein digitaler Kontaktkanal verfügbar."
        />
      {/if}
    </div>
    {#if planningPreviewError}
      <span class="planning-error-tag">
        <StatusTag
          size="sm"
          label="Planung nicht möglich"
          subLabel={planningPreviewError}
          color="red"
          tooltipContent={planningPreviewError}
          disableTitle={true}
        />
      </span>
    {/if}
  {/if}
</div>

<style lang="less">
  .planning-cell {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    min-width: 0;
  }

  .planning-tags {
    display: flex;
    align-items: flex-start;
    flex-wrap: nowrap;
    gap: 0.3rem;
  }

  .channel-preview {
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.2rem;
  }

  .confirmation-information-tag {
    display: inline-flex;
    align-self: flex-start;
    width: fit-content;
    max-width: 100%;
  }

  .confirmation-information-tag :global(.status-tag) {
    max-width: 100%;
  }

  .selectable-status-tag-button {
    display: inline-flex;
    padding: 0;
    border: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    cursor: pointer;
    min-width: 0;
    max-width: 100%;
  }

  .selectable-status-tag-button :global(.status-tag) {
    max-width: 100%;
  }

  .selectable-status-tag-button:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  .selectable-status-tag-button :global(.status-tag--tooltip) {
    cursor: pointer;
  }

  .selectable-status-tag-button:disabled :global(.status-tag--tooltip) {
    cursor: not-allowed;
  }

  .planning-error-tag {
    display: inline-flex;
    align-self: flex-start;
    max-width: 100%;
  }
</style>
